import {
  Badge,
  Box,
  Button,
  Code,
  Flex,
  Heading,
  Skeleton,
  Text,
} from '@radix-ui/themes';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { HardwareDiagnosticDetails } from 'components/machine/dialogs/hardware-diagnostic/details';
import { DataCollectorHoC } from 'components/machine/dialogs/installation/steps/data-collector';
import { MachineContext } from 'contexts/machine.context';
import { SubStatus } from 'enums/installation';
import { t } from 'i18next';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IDataCollection } from 'lib_ts/interfaces/i-data-collection';
import { useContext, useEffect, useState } from 'react';
import { DataCollectionsService } from 'services/data-collections.service';
import { PitchListsService } from 'services/pitch-lists.service';
import useMiscStore from 'stores/use-misc-store';

const COMPONENT_NAME = 'HardwareDiagnosticDialog';

export const HardwareDiagnosticDialog = (props: {
  hwd: IDataCollection;
  onClose: () => void;
}) => {
  const { machine, update: updateMachine } = useContext(MachineContext);

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(SubStatus.NotStarted);
  const [listName, setListName] = useState<string>();

  // check and align ball type at mount
  useEffect(() => {
    if (props.hwd.ball_type === machine.ball_type) {
      return;
    }

    if (props.hwd.complete || props.hwd.cancelled) {
      // skip things that are not in progress anymore
      return;
    }

    updateMachine({
      _id: machine._id,
      ball_type: props.hwd.ball_type,
    });
  }, []);

  // fetch list details so we can display the name
  useEffect(() => {
    const callback = async () => {
      setLoading(true);

      const list = await PitchListsService.getInstance().getOne(
        props.hwd.listID
      );

      setListName(list?.name);
      setLoading(false);
    };

    callback();
  }, [props.hwd.listID]);

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonDialog
        identifier={COMPONENT_NAME}
        title={
          <Flex direction="column" gap="1">
            <Flex gap="2" align="center">
              <Heading size="5">{t('common.hardware-diagnostic')}</Heading>
              <Badge size="1" variant="soft">
                {machine.machineID}
              </Badge>
            </Flex>

            {listName && (
              <Text color={RADIX.COLOR.HINT} size={RADIX.TEXT.SIZE.HINT}>
                {t('common.pitch-list')}: {listName}
              </Text>
            )}
          </Flex>
        }
        width={RADIX.DIALOG.WIDTH.LG}
        content={
          <ErrorBoundary identifier={COMPONENT_NAME}>
            <Flex
              direction="column"
              gap="4"
              style={{
                minHeight: '284px',
                height: '100%',
              }}
            >
              {status === SubStatus.NotStarted && (
                <Flex
                  flexGrow="1"
                  direction="column"
                  gap="5"
                  align="center"
                  justify="center"
                >
                  <Box>
                    <img
                      style={{
                        width: '176px',
                      }}
                      src={useMiscStore
                        .getState()
                        .getThemeImgPath('/renders/repeatability.svg')}
                    />
                  </Box>
                  <Text align="center" color={RADIX.COLOR.SECONDARY}>
                    You will now throw 3 pitch types, 6 times each.
                    <br />
                    Please ensure your tracking device is calibrated.
                  </Text>
                  <Box>
                    <Button onClick={() => setStatus(SubStatus.InProgress)}>
                      Start Hardware Diagnostic
                    </Button>
                  </Box>
                </Flex>
              )}

              {status === SubStatus.InProgress && props.hwd.collectionID && (
                <Skeleton loading={loading}>
                  <DataCollectorHoC
                    identifier="HwdDataCollector"
                    // for drawing error instead of complete
                    state={status}
                    id={props.hwd._id}
                    onComplete={async () => {
                      if (!props.hwd.complete) {
                        // mark this HWD as complete
                        await DataCollectionsService.updateOne(props.hwd._id, {
                          complete: true,
                        });
                      }

                      setStatus(SubStatus.Completed);
                    }}
                  />
                </Skeleton>
              )}

              {status === SubStatus.Completed && (
                <Flex
                  flexGrow="1"
                  direction="column"
                  gap="5"
                  align="center"
                  justify="center"
                >
                  <Box>
                    <img
                      height="80px"
                      alt="green checkmark in circle"
                      src="/img/check-circled-outlined.svg"
                    />
                  </Box>

                  <Text align="center" color={RADIX.COLOR.SECONDARY}>
                    The Hardware Diagnostic test is done, Customer Support will
                    be in contact soon.
                    <br />
                    Your reference number is{' '}
                    <Code
                      title="For reference, this is the collection ID for this test"
                      color={RADIX.COLOR.ACCENT}
                    >
                      {props.hwd.collectionID}
                    </Code>
                    .
                  </Text>

                  <Box>
                    <Button onClick={() => props.onClose()}>Done</Button>
                  </Box>
                </Flex>
              )}
            </Flex>
          </ErrorBoundary>
        }
        leftFooter={<HardwareDiagnosticDetails hwd={props.hwd} />}
        onClose={props.onClose}
      />
    </ErrorBoundary>
  );
};
